import { useRef, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import useCarousel from "@/hooks/useCarousel";
import { newsList } from "@/mocks/news";
import { formatChineseDate } from "@/utils/format";

export default function HomeHero() {
  const highlights = newsList.slice(0, 3);
  const { index, setIndex, next, prev, paused, setPaused } = useCarousel({
    length: highlights.length,
    interval: 6500,
  });
  const touchStartX = useRef<number | null>(null);
  const active = highlights[index];

  const onTouchStart = (event: TouchEvent<HTMLElement>) => {
    touchStartX.current = event.touches[0].clientX;
    setPaused(true);
  };

  const onTouchEnd = (event: TouchEvent<HTMLElement>) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (delta > 50) prev();
    if (delta < -50) next();
    touchStartX.current = null;
    setPaused(false);
  };

  return (
    <section
      aria-label="焦点新闻轮播"
      className="relative w-full overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative h-[460px] w-full sm:h-[520px] lg:h-[620px]">
        {highlights.map((item, i) => (
          <div
            key={item.id}
            aria-hidden={i !== index}
            className={`absolute inset-0 transition-opacity duration-[900ms] ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.cover}
              alt={`${item.title} - 张柏芝焦点新闻大图`}
              title={`${item.title} - 张柏芝最新资讯`}
              className={`h-full w-full object-cover object-top ${i === index ? "animate-slow-zoom" : ""}`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/45 to-foreground-950/10"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/25"></div>
          </div>
        ))}

        <div className="relative z-10 flex h-full w-full items-end px-4 pb-8 md:items-center md:px-8 md:pb-0 lg:px-12">
          <div className="w-full max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 text-[11px] tracking-wide text-background-200">
              <span className="rounded-full bg-primary-500 px-3 py-1 text-[11px] text-background-50">
                {active.category}
              </span>
              <span>{formatChineseDate(active.publishedAt)}</span>
              <span className="text-background-400">|</span>
              <span>{active.source}</span>
            </div>
            <h2 className="mt-5 font-heading text-2xl font-semibold leading-snug text-background-50 sm:text-3xl lg:text-[42px] lg:leading-tight">
              <Link to={`/news/${active.id}`} className="transition-opacity hover:opacity-85">
                {active.title}
              </Link>
            </h2>
            <p className="mt-4 hidden max-w-xl text-sm leading-7 text-background-200 sm:block">
              {active.summary}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/news/${active.id}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                阅读全文
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
              <Link
                to="/news"
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-5 py-2.5 text-sm text-background-100 transition-colors hover:border-background-50/70 hover:bg-background-50/10"
              >
                全部新闻
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 right-4 z-20 hidden items-center gap-4 md:right-8 md:flex lg:right-12">
          <button
            type="button"
            onClick={prev}
            aria-label="上一条焦点新闻"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
          </button>
          <div className="flex items-center gap-2">
            {highlights.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`切换到第 ${i + 1} 条焦点新闻`}
                className={`h-1 cursor-pointer rounded-full transition-all duration-300 ${
                  i === index ? "w-8 bg-primary-500" : "w-4 bg-background-50/45 hover:bg-background-50/70"
                }`}
              ></button>
            ))}
          </div>
          <button
            type="button"
            onClick={next}
            aria-label="下一条焦点新闻"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-right-s-line text-lg" aria-hidden="true"></i>
          </button>
        </div>

        <div className="absolute bottom-6 left-4 z-20 flex items-center gap-2 md:hidden">
          {highlights.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到第 ${i + 1} 条焦点新闻`}
              className={`h-1 cursor-pointer rounded-full transition-all ${
                i === index ? "w-7 bg-primary-500" : "w-3 bg-background-50/50"
              }`}
            ></button>
          ))}
        </div>

        {paused && (
          <span className="absolute left-4 top-4 z-20 hidden items-center gap-1.5 rounded-full bg-foreground-950/55 px-3 py-1 text-[11px] text-background-100 md:flex">
            <i className="ri-pause-circle-line" aria-hidden="true"></i>
            已暂停轮播
          </span>
        )}
      </div>
    </section>
  );
}